import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail  
} from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import './Login.css';
import { getNezoAppUrl } from '../utils/nezoApp';
const API_BASE = process.env.REACT_APP_API_BASE_URL || ''; 
// Nezo web app — "Continue with Nezo" signs in through it (see components/NezoSso.js); the Nezo the
// user last came from (staging or prod), else prod.

const Login = () => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [availableRegions, setAvailableRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regionsLoading, setRegionsLoading] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  // Where ProtectedRoute bounced the user from — honoured after sign-in instead of the role
  // dashboard, so a deep link (e.g. /composite-requests) survives the login page.
  const fromPath = location.state?.from?.pathname;
  const returnPath = fromPath && !['/', '/login'].includes(fromPath)
    ? fromPath + (location.state.from.search || '')
    : null;
  const db = getFirestore();

  // Add this useEffect to fetch regions on component mount
useEffect(() => {
  const fetchRegions = async () => {
    try {
      const response = await fetch(`${API_BASE}/regions`);
      const regions = await response.json();
      setAvailableRegions(regions);
      
      // Set default region (first one or user's last used)
      const defaultRegion = localStorage.getItem('selectedRegion') || regions[0]?.id;
      setSelectedRegion(defaultRegion);
    } catch (error) {
      console.error('❌ Error fetching regions:', error);
      // Fallback regions
      setAvailableRegions([
        { id: 'vquest', name: 'New Zealand', country_code: 'NZ' }
      ]);
      setSelectedRegion('vquest');
    } finally {
      setRegionsLoading(false);
    }
  };

  fetchRegions();
}, []);

  // Move these functions to use useCallback for proper dependency management
  const getUserRole = useCallback(async (userId, retryCount = 0) => {
    try {
      console.log(`🔍 Fetching user role for: ${userId} (attempt ${retryCount + 1})`);
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('📊 Fetched user data:', userData);
        
        if (!userData.role) {
          console.warn('⚠️ User document missing role field, defaulting to user');
        }
        
        return {
          role: userData.role || 'user',
          status: userData.status || 'active',
          ...userData
        };
      } else {
        console.warn('⚠️ User document does not exist in Firestore');
        
        if (retryCount < 2) {
          console.log('🔄 Retrying user data fetch...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return getUserRole(userId, retryCount + 1);
        }
        
        return { role: 'user', status: 'active' };
      }
    } catch (error) {
      console.error('❌ Error fetching user role:', error);
      
      if (retryCount < 2 && (error.code === 'unavailable' || error.code === 'permission-denied')) {
        console.log('🔄 Retrying due to network error...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return getUserRole(userId, retryCount + 1);
      }
      
      return { role: 'user', status: 'active' };
    }
  }, [db]);

  const debugUserRole = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      console.log('🔍 Current Firebase Auth User:', user.uid);
      const userData = await getUserRole(user.uid);
      console.log('📊 Fetched User Data:', userData);
    }
  }, [getUserRole]);

  const redirectBasedOnRole = useCallback(async (user) => {
    console.log('🔄 Starting role-based redirect for:', user.uid);
    const userData = await getUserRole(user.uid);
    
    console.log('👤 User role data:', {
      role: userData.role,
      status: userData.status,
      email: userData.email
    });
    
    if (userData.status === 'suspended' || userData.status === 'rejected') {
      console.log('🚫 User account is suspended/rejected');
      setError('Your account has been suspended. Please contact support.');
      await signOut(auth);
      return;
    }

    console.log(`🎯 Redirecting based on role: ${userData.role}, status: ${userData.status}`);
    
    switch (userData.role) {
      case 'admin':
        console.log('➡️ Redirecting to admin dashboard');
        navigate(returnPath || '/dashboard');
        break;
      case 'supplier':
        if (userData.status === 'pending') {
          console.log('➡️ Redirecting to supplier pending approval');
          navigate('/supplier/pending-approval');
        } else {
          console.log('➡️ Redirecting to supplier dashboard');
          navigate(returnPath || '/supplier/dashboard');
        }
        break;
      case 'user':
        if (userData.status === 'pending') {
          console.log('➡️ Redirecting to user pending approval');
          navigate('/user/pending-approval');
        } else {
          console.log('➡️ Redirecting to user dashboard');
          navigate(returnPath || '/user/dashboard');
        }
        break;
      default:
        console.log('⚠️ Unknown role, defaulting to user dashboard');
        navigate('/user/dashboard');
    }
  }, [auth, navigate, getUserRole, returnPath]);

  const refreshUserData = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      await redirectBasedOnRole(user);
    }
  }, [redirectBasedOnRole]);

  const exposeDebugFunctions = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      window.debugUserRole = debugUserRole;
      window.refreshUserData = refreshUserData;
      console.log('🔧 Debug functions exposed: window.debugUserRole(), window.refreshUserData()');
    }
  }, [debugUserRole, refreshUserData]);

  useEffect(() => {
    exposeDebugFunctions();
  }, [exposeDebugFunctions]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user && !loading) {
          console.log('👤 Auth state changed - user signed in:', user.uid);
          await redirectBasedOnRole(user);
        } else if (!user) {
          console.log('👤 Auth state changed - user signed out');
        }
      } catch (error) {
        console.error('❌ Error in auth state change handler:', error);
        setError('Authentication error. Please try signing in again.');
      }
    });
    
    return () => unsubscribe();
  }, [auth, loading, redirectBasedOnRole]);



    const sendEmailToAdmins = async ({ email, role, userId }) => {
      try {
        const res = await fetch(`${API_BASE}/public/notify-registration`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userName: email.split('@')[0], // Extract name from email
            userEmail: email,
            userRole: role,
            userId
          })
        });
        
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        
        const result = await res.json();
        return { success: true, result };
      } catch (error) {
        console.error('❌ Email send failed:', error);
        return { success: false, error: error.message };
      }
    };

    const sendAdminNotification = async (data) => {
      console.log('🔔 Sending admin notification:', data);
      
      try {
        // Create Firestore notification (unchanged)
        const notificationId = Date.now().toString();
        await setDoc(doc(db, 'admin_notifications', notificationId), {
          id: notificationId,
          type: data.type,
          email: data.email,
          userId: data.userId,
          createdAt: new Date().toISOString(),
          read: false,
          priority: 'high'
        });

        console.log('✅ Firestore notification created');

        // Send email notification via Nodemailer
        console.log('📧 Attempting to send email via Nodemailer...');
        const emailResult = await sendEmailToAdmins({
          email: data.email,
          role: data.type.replace('new_', '').replace('_registration', ''),
          userId: data.userId
        });

        if (emailResult.success) {
          console.log('✅ Nodemailer email notification sent successfully');
        } else {
          console.error('⚠️ Nodemailer email notification failed:', emailResult.error);
          // Continue with registration even if email fails
        }

      } catch (error) {
        console.error('❌ Error in sendAdminNotification:', error);
        throw error;
      }
    };


  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!selectedRegion) {
    setError('Please select a region');
    setLoading(false);
    return;
  }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // ✅ VALIDATE REGION ACCESS
      const hasRegionAccess = await validateUserRegionAccess(user, selectedRegion);
    
      if (hasRegionAccess) {
        // Store region selection
        localStorage.setItem('selectedRegion', selectedRegion);
        
        await setDoc(doc(db, 'users', user.uid), 
          { 
            lastLogin: new Date().toISOString(),
            lastRegion: selectedRegion 
          }, 
          { merge: true }
        );
        
        await redirectBasedOnRole(user);
      } else {
        // Region access denied - error already set in validateUserRegionAccess
        setLoading(false);
        return;
      }
    
      } catch (error) {
        setError(getErrorMessage(error.code));
      } finally {
        setLoading(false);
    }
};

const handleRegister = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');
  setSuccess('');

  if (password !== confirmPassword) {
    setError('Passwords do not match');
    setLoading(false);
    return;
  }

  if (password.length < 6) {
    setError('Password must be at least 6 characters long');
    setLoading(false);
    return;
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // SIMPLIFIED: All registrations are now pending (no admin self-registration)
    const userStatus = 'pending';
    
    const userData = {
      email: email,
      role: selectedRole, // Will only be 'user' or 'supplier' now
      status: userStatus,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      lastRegion: selectedRegion, // ✅ Already have this
        allowedRegions: [selectedRegion], // Array of regions user can access
      defaultRegion: selectedRegion,    // User's primary/default region
      regionAccess: {
        [selectedRegion]: {
          assignedAt: new Date().toISOString(),
          assignedBy: 'self-registration', // or admin email
          status: 'active'
        }
      },
      profileCompleted: false,
      companyProfile: {
        companyName: '',
        primaryContact: '',
        phoneNumber: '',
        email: email,
        website: '',
        completedAt: null,
        operatingRegions: [selectedRegion],
        primaryRegion: selectedRegion
      }
    };

    await setDoc(doc(db, 'users', user.uid), userData);

    // SIMPLIFIED: Send admin notification for all registrations (user and supplier)
    await sendAdminNotification({
      type: `new_${selectedRole}_registration`,
      email: email,
      userId: user.uid
    });
    
  } catch (error) {
    setError(getErrorMessage(error.code));
  } finally {
    setLoading(false);
  }
};
const handleForgotPassword = async (e) => {
  e.preventDefault();
  setForgotPasswordLoading(true);
  setForgotPasswordError('');
  setForgotPasswordMessage('');

  if (!forgotPasswordEmail) {
    setForgotPasswordError('Please enter your email address');
    setForgotPasswordLoading(false);
    return;
  }

  try {
    await sendPasswordResetEmail(auth, forgotPasswordEmail);
    setForgotPasswordMessage('Password reset email sent! Check your inbox and spam folder.');
    setForgotPasswordEmail('');
    
    // Close modal after 3 seconds
    setTimeout(() => {
      setShowForgotPassword(false);
      setForgotPasswordMessage('');
    }, 3000);
    
  } catch (error) {
    console.error('Password reset error:', error);
    
    switch (error.code) {
      case 'auth/user-not-found':
        setForgotPasswordError('No account found with this email address.');
        break;
      case 'auth/invalid-email':
        setForgotPasswordError('Invalid email address format.');
        break;
      case 'auth/too-many-requests':
        setForgotPasswordError('Too many requests. Please try again later.');
        break;
      default:
        setForgotPasswordError('Failed to send password reset email. Please try again.');
    }
  } finally {
    setForgotPasswordLoading(false);
  }
};

const closeForgotPasswordModal = () => {
  setShowForgotPassword(false);
  setForgotPasswordEmail('');
  setForgotPasswordError('');
  setForgotPasswordMessage('');
};


const validateUserRegionAccess = useCallback(async (user, selectedRegion) => {
  try {
    const userData = await getUserRole(user.uid);
    
    // Check if user has access to the selected region
    const allowedRegions = userData.allowedRegions || [userData.defaultRegion || 'vquest'];
    const hasAccess = allowedRegions.includes(selectedRegion);
    
    if (!hasAccess) {
      console.log(`❌ User ${user.email} denied access to region: ${selectedRegion}`);
      console.log(`✅ User allowed regions:`, allowedRegions);
      
      // Redirect to user's default region
      const defaultRegion = userData.defaultRegion || allowedRegions[0];
      setSelectedRegion(defaultRegion);
      localStorage.setItem('selectedRegion', defaultRegion);
      
      setError(`Access denied to selected region. Redirected to your assigned region: ${defaultRegion}`);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error validating region access:', error);
    return true; // Allow access on error to prevent lockout
  }
}, [getUserRole]);



  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters long.';
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const switchMode = () => {
    setIsLoginMode(!isLoginMode);
    resetForm();
  };

  return (
    <div className="modern-login-container">

      {/* Top-left logo */}
      <div className="login-logo">
        <img
          src="/icons/Nezo_Logo_White.png"
          alt="Nezo Ops Console"
        />
      </div>

      {/* Back button — top-right on green background */}
      <a href="https://app.nezo-app.com/" className="back-button-login">
        <img
          src="/icons/Nezo_Navy_Fabricon.png"
          alt="Back to Nezo"
          style={{ height: 38, width: 'auto' }}
        />
      </a>

      {/* Centred card */}
      <div className="login-form-section">
        <div className="form-container">
          <div className="form-header">
            <p className="form-subtitle">
              {isLoginMode ? 'Please enter your details' : 'Fill in the details to get started'}
            </p>
            <h2>{isLoginMode ? 'Welcome back' : 'Create Account'}</h2>
          </div>

          {error && (
            <div className="alert alert-error">
              <div className="alert-icon">⚠️</div>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <div className="alert-icon">✅</div>
              <div className="alert-content">
                {typeof success === 'string' ? (
                  <span>{success}</span>
                ) : (
                  <>
                    <div className="success-title">{success.title}</div>
                    <div className="success-details">
                      {success.message}
                      <br />
                      <br />
                      {success.details}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <form className="modern-form" onSubmit={isLoginMode ? handleLogin : handleRegister}>
            {!isLoginMode && (
              <div className="form-group">
                <label htmlFor="role" className="form-label">Account Type</label>
                <div className="select-wrapper">
                  <select 
                    id="role"
                    value={selectedRole} 
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="form-select"
                    disabled={loading}
                  >
                    <option value="user">User (Material Builder)</option>
                    <option value="supplier">Supplier (Material Provider)</option>
                    {/* <option value="admin">Admin (System Administrator)</option> */}
                  </select>
                  <div className="select-arrow">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                      <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Region Selection */}
              <div className="form-group">
                <label htmlFor="region" className="form-label">Region</label>
                <div className="select-wrapper">
                  {regionsLoading ? (
                    <div className="loading-regions">Loading regions...</div>
                  ) : (
                    <select 
                      id="region"
                      value={selectedRegion} 
                      onChange={(e) => {
                        setSelectedRegion(e.target.value);
                        localStorage.setItem('selectedRegion', e.target.value);
                      }}
                      className="form-select"
                      disabled={loading}
                      required
                    >
                      <option value="">Select Region</option>
                        {availableRegions.map((region) => {
                          const cc = region.country_code?.toUpperCase?.();
                          const label = cc ? `${region.name} (${cc})` : region.name;
                          return (
                            <option key={region.id} value={region.id}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                  )}
                  <div className="select-arrow">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                      <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <div className="input-wrapper">
                <div className="input-icon">
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="input-wrapper">
                <div className="input-icon">
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  minLength="6"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M14.12 14.12C12.95 15.04 11.49 15.5 10 15.5C4.5 15.5 1.5 10 1.5 10C2.73 7.74 4.53 5.82 6.88 4.88M8.21 3.73C8.8 3.6 9.4 3.5 10 3.5C15.5 3.5 18.5 10 18.5 10C17.89 11.17 17.09 12.18 16.17 13M12.12 7.88C12.49 8.25 12.74 8.74 12.74 9.26C12.74 10.35 11.85 11.24 10.76 11.24C10.24 11.24 9.75 10.99 9.38 10.62" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M1 1L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M1 10S4 4 10 4S19 10 19 10S16 16 10 16S1 10 1 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {!isLoginMode && (
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input"
                    placeholder="Confirm your password"
                    required
                    disabled={loading}
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.12 14.12C12.95 15.04 11.49 15.5 10 15.5C4.5 15.5 1.5 10 1.5 10C2.73 7.74 4.53 5.82 6.88 4.88M8.21 3.73C8.8 3.6 9.4 3.5 10 3.5C15.5 3.5 18.5 10 18.5 10C17.89 11.17 17.09 12.18 16.17 13M12.12 7.88C12.49 8.25 12.74 8.74 12.74 9.26C12.74 10.35 11.85 11.24 10.76 11.24C10.24 11.24 9.75 10.99 9.38 10.62" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1 1L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M1 10S4 4 10 4S19 10 19 10S16 16 10 16S1 10 1 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            <button 
              type="submit" 
              className="form-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-login"></div>
                  {isLoginMode ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                isLoginMode ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {isLoginMode && (
            <>
              <div className="nezo-sso-divider"><span>or</span></div>
              <a
                className="nezo-sso-btn"
                href={`${getNezoAppUrl()}/ops-handoff?returnTo=${encodeURIComponent(returnPath && returnPath.startsWith('/user/') ? returnPath : '/composite-requests')}`}
              >
                <img src="/icons/Nezo_Navy_Fabricon.png" alt="" aria-hidden="true" />
                Continue with Nezo
              </a>
              <p className="nezo-sso-hint">Use the account you sign in to Nezo with — no separate password needed.</p>
            </>
          )}

          <div className="form-footer">
            <p>
              {isLoginMode ? "Don't have an account? " : "Already have an account? "}
              <button 
                type="button"
                onClick={switchMode}
                className="switch-mode-btn"
                disabled={loading}
              >
                {isLoginMode ? 'Create one' : 'Sign in'}
              </button>
            </p>
            
            {isLoginMode && (
              <button 
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-link"
              >
                Forgot your password?
              </button>
            )}
            {/* Forgot Password Modal */}
              {showForgotPassword && (
                <div className="modal-overlay">
                  <div className="modal-content-reset-password">
                    <div className="modal-header-reset-password">
                      <h3>Reset Password</h3>
                      <button 
                        type="button" 
                        className="modal-close"
                        onClick={closeForgotPasswordModal}
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="modal-body">
                      <p className="modal-description">
                        Enter your email address and we'll send you a link to reset your password.
                      </p>
                      
                      {forgotPasswordError && (
                        <div className="alert alert-error">
                          <div className="alert-icon">⚠️</div>
                          <span>{forgotPasswordError}</span>
                        </div>
                      )}
                      
                      {forgotPasswordMessage && (
                        <div className="alert alert-success">
                          <div className="alert-icon">✅</div>
                          <span>{forgotPasswordMessage}</span>
                        </div>
                      )}
                      
                      <form onSubmit={handleForgotPassword}>
                          <div className="form-group">
                            <label htmlFor="forgotEmail" className="form-label"></label>
                            <div className="input-wrapper">
                              <div className="input-icon">
                              </div>
                              <input
                                type="email"
                                id="forgotEmail"
                                value={forgotPasswordEmail}
                                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                className="form-input"
                                placeholder="Enter your email address"
                                required
                                disabled={forgotPasswordLoading}
                              />
                            </div>
                          </div>
                          
                          <div className="modal-actions">
                            <button 
                              type="submit" 
                              className="btn-primary-reset-password"
                              disabled={forgotPasswordLoading}
                            >
                              {forgotPasswordLoading ? (
                                <>
                                  <div className="spinner-login"></div>
                                  Sending...
                                </>
                              ) : (
                                'Send Reset Link'
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;